import { useMemo } from "react";
import { CalendarEvent, EVENT_TYPE_CONFIG } from "@/hooks/useCalendarEvents";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarEventPopover } from "./CalendarEventPopover";
import { cn } from "@/lib/utils";

interface Props {
  selectedDate: Date;
  events: CalendarEvent[];
  layers: Record<string, boolean>;
}

function getWeekDates(d: Date) {
  const start = new Date(d);
  start.setDate(start.getDate() - start.getDay());
  return Array.from({ length: 7 }, (_, i) => {
    const day = new Date(start);
    day.setDate(day.getDate() + i);
    return day;
  });
}

function dateKey(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

const HOURS = Array.from({ length: 14 }, (_, i) => i + 6); // 6AM to 7PM

export const CalendarWeekView = ({ selectedDate, events, layers }: Props) => {
  const weekDays = useMemo(() => getWeekDates(selectedDate), [selectedDate]);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const filtered = events.filter((e) => layers[e.event_type] !== false);

  const allDayByDate = useMemo(() => {
    const map: Record<string, CalendarEvent[]> = {};
    filtered
      .filter((e) => e.all_day)
      .forEach((e) => {
        const start = new Date(e.start_date + "T00:00:00");
        const end = e.end_date ? new Date(e.end_date + "T00:00:00") : start;
        const cur = new Date(start);
        while (cur <= end) {
          const k = dateKey(cur);
          if (!map[k]) map[k] = [];
          map[k].push(e);
          cur.setDate(cur.getDate() + 1);
        }
      });
    return map;
  }, [filtered]);

  const timedByDate = useMemo(() => {
    const map: Record<string, CalendarEvent[]> = {};
    filtered
      .filter((e) => !e.all_day && e.start_time)
      .forEach((e) => {
        const k = e.start_date;
        if (!map[k]) map[k] = [];
        map[k].push(e);
      });
    return map;
  }, [filtered]);

  return (
    <div className="flex-1 overflow-auto">
      {/* Column headers */}
      <div className="grid grid-cols-[60px_repeat(7,1fr)] border-b sticky top-0 bg-card z-10">
        <div className="border-r" />
        {weekDays.map((d) => {
          const isToday = dateKey(d) === dateKey(today);
          return (
            <div key={dateKey(d)} className="border-r p-1 text-center">
              <div className="text-[10px] text-muted-foreground uppercase">
                {d.toLocaleDateString("en-US", { weekday: "short" })}
              </div>
              <div
                className={cn(
                  "text-sm font-semibold mx-auto h-6 w-6 flex items-center justify-center rounded-full",
                  isToday && "bg-primary text-primary-foreground"
                )}
              >
                {d.getDate()}
              </div>
            </div>
          );
        })}
      </div>

      {/* All-day row */}
      <div className="grid grid-cols-[60px_repeat(7,1fr)] border-b bg-muted/30">
        <div className="border-r text-[9px] text-muted-foreground p-1">all day</div>
        {weekDays.map((d) => {
          const k = dateKey(d);
          const dayEvents = allDayByDate[k] || [];
          return (
            <div key={k} className="border-r p-0.5 min-h-[28px] space-y-0.5">
              {dayEvents.slice(0, 2).map((ev) => {
                const cfg = EVENT_TYPE_CONFIG[ev.event_type];
                return (
                  <Popover key={ev.id}>
                    <PopoverTrigger asChild>
                      <button className={cn("w-full text-[9px] px-1 py-0.5 rounded truncate border-l-2 text-left", cfg.bgClass, cfg.textClass, cfg.borderClass)}>
                        {ev.title}
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-72 p-0"><CalendarEventPopover event={ev} /></PopoverContent>
                  </Popover>
                );
              })}
              {dayEvents.length > 2 && (
                <span className="text-[8px] text-muted-foreground pl-1">+{dayEvents.length - 2}</span>
              )}
            </div>
          );
        })}
      </div>

      {/* Time slots */}
      {HOURS.map((hour) => (
        <div key={hour} className="grid grid-cols-[60px_repeat(7,1fr)] border-b min-h-[48px]">
          <div className="border-r text-[10px] text-muted-foreground p-1 text-right pr-2">
            {hour === 0 ? "12 AM" : hour < 12 ? `${hour} AM` : hour === 12 ? "12 PM" : `${hour - 12} PM`}
          </div>
          {weekDays.map((d) => {
            const k = dateKey(d);
            const dayTimed = timedByDate[k] || [];
            const inSlot = dayTimed.filter((e) => {
              if (!e.start_time) return false;
              const h = parseInt(e.start_time.split(":")[0]);
              return h === hour;
            });
            return (
              <div key={k} className="border-r p-0.5">
                {inSlot.map((ev) => {
                  const cfg = EVENT_TYPE_CONFIG[ev.event_type];
                  return (
                    <Popover key={ev.id}>
                      <PopoverTrigger asChild>
                        <button className={cn("w-full text-[9px] px-1 py-0.5 rounded truncate border-l-2 text-left", cfg.bgClass, cfg.textClass, cfg.borderClass)}>
                          {ev.start_time?.slice(0, 5)} {ev.title}
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-72 p-0"><CalendarEventPopover event={ev} /></PopoverContent>
                    </Popover>
                  );
                })}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
};
